import type { CSSProperties } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { DREAM_COMPONENTS_Z_INDEX_VALUES } from '@/constants';
import type { NavbarMenuElementProps } from '@/navbarElement/Navbar.type';
import { getBackgroundColor } from '@/utils/background';
import { cn } from '@/utils/cn';

import * as styles from '../Navbar.css';
import {
  computeHiddenOnScroll,
  getNavbarScrollVariantStyles,
  NavbarScrollContext,
  type NavbarScrollStyle,
  useNavbarScrollStore,
} from '../navbarScroll';
import { NAVBAR_MENU_DEFAULTS_V2 } from './NavbarMenu.constants';

const DEFAULT_BOX_SHADOW = '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)';
const DEFAULT_PLACEHOLDER_HEIGHT = '68px';

const BASE_FLEX_WRAPPER_STYLE: Pick<CSSProperties, 'display' | 'justifyContent' | 'left' | 'right'> = {
  display: 'flex',
  justifyContent: 'center',
  left: 0,
  right: 0,
};

export function NavbarMenuV2(props: NavbarMenuElementProps) {
  const { element, children, className, attributes, isInEditor } = props;
  const {
    background,
    shadow,
    style,
    customAttributes,
    borderWidth,
    borderStyle,
    borderColor,
    stickToTop,
    stickyIsland,
    borderRadius,
    stickyBlurBackground,
    stickyBlurOpacity,
    stickyPadding,
    stickyMaxWidth,
    stickyBorderRadius,
    firstSectionBackgroundColor,
    hideOnScrollDown,
    scrollStyle: scrollStyleAttr,
    scrollTriggerOffset,
  } = element.attrs || {};

  const scrollStyle: NavbarScrollStyle =
    scrollStyleAttr === 'solid' || scrollStyleAttr === 'glass' ? scrollStyleAttr : 'none';
  const scrollTrigger = scrollTriggerOffset ?? NAVBAR_MENU_DEFAULTS_V2.scrollTriggerOffset;

  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const heightPlaceholderRef = useRef<HTMLDivElement>(null);
  const [stickToTopWrapperHeight, setStickToTopWrapperHeight] = useState(DEFAULT_PLACEHOLDER_HEIGHT);

  const contentLength = element?.content?.length || 0;
  const isSticky = Boolean(stickToTop);
  const isStickyIsland = isSticky && stickyIsland;
  const isStickyWithPlaceholder = isSticky && !stickyIsland && !isInEditor;

  const needsScrollStore = !isInEditor && isSticky && (Boolean(hideOnScrollDown) || scrollStyle !== 'none');
  const scroll = useNavbarScrollStore(needsScrollStore);
  const hideOnScrollStateRef = useRef(false);

  const isScrolledPastStyleOffset = scroll.scrollY > scrollTrigger;
  const isHiddenOnScroll = computeHiddenOnScroll(
    scroll,
    needsScrollStore && Boolean(hideOnScrollDown),
    hideOnScrollStateRef,
  );

  const scrollContextValue = useMemo(
    () => ({ isScrolledPastOffset: isScrolledPastStyleOffset, scrollStyle }),
    [isScrolledPastStyleOffset, scrollStyle],
  );

  const baseNavStyle = useMemo(() => {
    const resultingBorderRadius = stickToTop
      ? (stickyBorderRadius ?? NAVBAR_MENU_DEFAULTS_V2.stickyBorderRadius)
      : (borderRadius ?? NAVBAR_MENU_DEFAULTS_V2.borderRadius);

    const resolvedBackground = getBackgroundColor(background ?? NAVBAR_MENU_DEFAULTS_V2.background);
    const blurAmount = stickyBlurBackground ?? NAVBAR_MENU_DEFAULTS_V2.stickyBlurBackground;
    const hasStickyBlur = Boolean(stickToTop) && Number.parseFloat(blurAmount) > 0;
    const blurOpacity = stickyBlurOpacity ? Number(stickyBlurOpacity) / 100 : 0.3;

    return {
      width: '100%',
      ...(style === 'border'
        ? {
            borderColor: borderColor ?? NAVBAR_MENU_DEFAULTS_V2.borderBorderColor,
            borderWidth: borderWidth ?? NAVBAR_MENU_DEFAULTS_V2.borderBorderWidth,
            borderStyle: borderStyle ?? NAVBAR_MENU_DEFAULTS_V2.borderBorderStyle,
          }
        : {}),
      '--box-shadow': style === 'shadow' ? shadow || DEFAULT_BOX_SHADOW : 'none',
      '--background': resolvedBackground,
      ...(hasStickyBlur ? { background: `rgba(255,255,255,${blurOpacity})` } : {}),
      '--border-radius': resultingBorderRadius,
      ...(contentLength === 2
        ? {
            '--list-1-border-radius': `${resultingBorderRadius} ${resultingBorderRadius} 0 0`,
            '--list-2-border-radius': `0 0 ${resultingBorderRadius} ${resultingBorderRadius}`,
          }
        : { '--list-1-border-radius': resultingBorderRadius }),
      flexDirection: 'column',
      ...(stickToTop && {
        maxWidth: stickyIsland ? (stickyMaxWidth ?? NAVBAR_MENU_DEFAULTS_V2.stickyMaxWidth) : '100%',
        '--blur-background': stickyBlurBackground
          ? `${stickyBlurBackground}`
          : `${NAVBAR_MENU_DEFAULTS_V2.stickyBlurBackground}px`,
        '--blur-opacity': `${blurOpacity}`,
      }),
    } as CSSProperties & Record<string, string | number | undefined>;
  }, [
    background,
    borderColor,
    borderRadius,
    borderStyle,
    borderWidth,
    shadow,
    style,
    stickToTop,
    stickyBlurBackground,
    stickyBlurOpacity,
    stickyBorderRadius,
    stickyIsland,
    stickyMaxWidth,
    contentLength,
  ]);

  const scrollVariantStyle = useMemo(() => {
    if (!needsScrollStore || scrollStyle === 'none' || !isScrolledPastStyleOffset) {
      return undefined;
    }

    return getNavbarScrollVariantStyles(scrollStyle, {
      background,
      borderWidth: borderWidth != null ? String(borderWidth) : undefined,
      borderStyle,
      borderColor,
      style,
    });
  }, [
    needsScrollStore,
    scrollStyle,
    isScrolledPastStyleOffset,
    background,
    borderWidth,
    borderStyle,
    borderColor,
    style,
  ]);

  const navStyle = scrollVariantStyle ? { ...baseNavStyle, ...scrollVariantStyle } : baseNavStyle;

  const wrapperStyle = useMemo(() => {
    if (!isStickyIsland) return undefined;

    return {
      ...BASE_FLEX_WRAPPER_STYLE,
      position: isInEditor ? 'relative' : 'fixed',
      ...(isInEditor && { width: '100%' }),
      padding: stickyPadding ?? '0px',
      zIndex: DREAM_COMPONENTS_Z_INDEX_VALUES.stickyNavbarIslandWrapper,
    } as CSSProperties;
  }, [isStickyIsland, isInEditor, stickyPadding]);

  const stickToTopBackgroundPlaceholderStyles = useMemo(() => {
    return {
      ...BASE_FLEX_WRAPPER_STYLE,
      top: 0,
      position: 'absolute',
      backgroundColor: firstSectionBackgroundColor,
      height: stickToTopWrapperHeight,
    } as CSSProperties;
  }, [firstSectionBackgroundColor, stickToTopWrapperHeight]);

  const cleanupResizeObserver = useCallback(() => {
    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect();
      resizeObserverRef.current = null;
    }
  }, []);

  const observeNavHeight = useCallback(
    (navElement: HTMLElement | null) => {
      cleanupResizeObserver();

      if (!navElement || !isStickyWithPlaceholder) return;

      if (heightPlaceholderRef.current && navElement.clientHeight) {
        setStickToTopWrapperHeight(`${navElement.clientHeight}px`);
      }

      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const height = entry.borderBoxSize?.[0]?.blockSize ?? entry.target.clientHeight;
          setStickToTopWrapperHeight(`${height}px`);
        }
      });

      resizeObserver.observe(navElement);
      resizeObserverRef.current = resizeObserver;
    },
    [isStickyWithPlaceholder, cleanupResizeObserver],
  );

  useEffect(() => cleanupResizeObserver, [cleanupResizeObserver]);

  const hideOnScrollClasses = cn(
    needsScrollStore && styles.navbarMenuScrollStyleTransitionV2,
    isHiddenOnScroll && styles.navbarMenuHiddenOnScrollV2,
  );

  const NavEl = (
    <nav
      ref={observeNavHeight}
      {...props}
      role="presentation"
      {...customAttributes}
      className={cn(
        className,
        customAttributes?.className,
        styles.navbarMenuV2,
        isSticky && !isInEditor && styles.navbarMenuStickyPlacementV2,
        isSticky && styles.navbarMenuBackgroundV2,
        !isSticky && [styles.navigationMenuRelativeV2, !isInEditor && styles.navigationMenuRelativeMinWidthV2],
        !isStickyIsland && hideOnScrollClasses,
      )}
      style={navStyle}
      {...attributes}
    >
      {children}
    </nav>
  );

  const wrappedNavEl =
    needsScrollStore && scrollStyle !== 'none' ? (
      <NavbarScrollContext.Provider value={scrollContextValue}>{NavEl}</NavbarScrollContext.Provider>
    ) : (
      NavEl
    );

  if (isStickyIsland) {
    return (
      <div style={wrapperStyle} className={hideOnScrollClasses}>
        {wrappedNavEl}
      </div>
    );
  }

  if (isStickyWithPlaceholder) {
    return (
      <>
        {wrappedNavEl}
        <div ref={heightPlaceholderRef} style={stickToTopBackgroundPlaceholderStyles} />
      </>
    );
  }

  return wrappedNavEl;
}
